import PropTypes from "prop-types"
import React, {useState, useRef, useEffect} from "react"
import Styled, {css, keyframes} from "styled-components"
import {backgrounds, extended, primary} from "@uprise/colors"
import {spacing} from "@uprise/spacing"
import {P} from "@uprise/text"

// Components
import Link from "./Link"

const flipInX = keyframes`
      from {
            -webkit-transform: perspective(400px) rotate3d(1, 0, 0, 90deg);
            transform: perspective(400px) rotate3d(1, 0, 0, 90deg);
            -webkit-animation-timing-function: ease-in;
            animation-timing-function: ease-in;
            opacity: 0;
      }

      to {
            -webkit-transform: perspective(400px);
            transform: perspective(400px);
      }
`

const DropDownWrapper = Styled.ul`
      display: inline-block;
      list-style-type: none;
      margin: ${props => props.margin};
      padding: 0;
 
      &:hover {
            cursor: pointer;
      }
`

const ItemLvl1 = Styled(P)`
      color: ${props => (props.white ? backgrounds.white : extended.charcoal.one)};
      
      @media (max-width: 1024px) {
            &:after {
                  content: ' ';
                  position: absolute;
                  right: 20px;
                  display: inline-block;
                  height: 14px;
                  width: 14px;
                  background-image: ${props => `url(${require(`../../images/icons/${props.icon}`)})`};
                  
                  background-size: contain;
                  background-repeat: no-repeat;
            }
 	}
`

const DropDownItems = Styled(DropDownWrapper)`
      margin: 15px 0 0 0;
      background: ${props => (props.white ? backgrounds.fadedPurple : backgrounds.white)};
      padding: ${spacing.s6};
      border-radius: 5px;
      box-shadow: ${props => (props.white ? "0;" : `0 0 3px -1px rgba(0, 0, 0, 0.3);`)}

      :after {
            top: -16px;
            left: 50%;
            border: solid transparent;
            content: " ";
            height: 0;
            width: 0;
            position: absolute;
            pointer-events: none;
            border-color: rgba(255, 255, 255, 0);
            border-bottom-color: ${props => (props.white ? backgrounds.fadedPurple : backgrounds.white)};
            border-width: 17px;
            margin-left: -17px;
      }

      :before {
            top: -16px;
            left: 50%;
            border: solid transparent;
            content: " ";
            height: 0;
            width: 0;
            position: absolute;
            pointer-events: none;
            border-color: rgba(255, 255, 255, 0);
            border-bottom-color: rgba(0, 0, 0, 0.05);
            border-width: 15px;
            margin-left: -15px;
      }

      @media (max-width: 1024px) {
            margin: 0 0 0 0;
            background: transparent;
            box-shadow: none;

            &:before {
                  display: none;
            }

            &:after {
                  display: none;
            }
 	}
`

const DropDownItemsWrap = Styled.div`
      margin-left: ${props => `-${props.center}px`};
      z-index: 10;
      position: absolute; 
      display: block;
      visibility: ${props => (props.show ? "visible" : "hidden")};
      animation: ${props =>
			props.show
				? css`
						${flipInX} 0.3s linear
				  `
				: ""};

      @media (max-width: 1024px) {
            position: relative;
            margin-left: 0;
 	}
`

const DropDownItemLvl1 = Styled.li`
      &:last-child { 
            margin-bottom: 0 !important;
      }

      &:hover {
		cursor: pointer;
      }

      @media (max-width: 1024px) {
            color: ${props => (props.white ? backgrounds.white : extended.charcoal.one)} !important;
      }
`

const DropDownItemLvl2 = Styled.li`
      &:last-child { 
            margin-bottom: 0 !important;
      }

      &:hover {
            cursor: pointer;
      }

      @media (max-width: 1024px) {
            & > a {
                  color: ${props => (props.white ? backgrounds.white : extended.charcoal.one)} !important;
            }
      }
`

const DropDown = ({dropdown, margin, fontSize, white, fontSizeHover, color, icon}) => {
	const [showDropdown, setShowDropDown] = useState(false)
	const [center, setCenter] = useState(0)

	const dropDown = useRef(null)
	const lvl1Text = useRef(null)

	const _handleOut = () => {
		setShowDropDown(false)
	}

	const _handleIn = () => {
		setShowDropDown(true)
	}

	useEffect(() => {
		const dropdownWidth = dropDown?.current?.clientWidth
		const textWidth = lvl1Text?.current?.clientWidth
		const gap = dropdownWidth / 2 - textWidth
		const middle = gap + textWidth / 2
		setCenter(middle)
	}, [showDropdown])

	return (
		<DropDownWrapper margin={margin}>
			<DropDownItemLvl1 onMouseLeave={() => _handleOut()}>
				<ItemLvl1
					myRef={lvl1Text}
					fontSize={fontSize}
					white={white}
					color={color}
					onMouseEnter={() => _handleIn()}
					lineHeight='auto'
					icon={icon}>
					{dropdown.title}
				</ItemLvl1>
				<DropDownItemsWrap ref={dropDown} show={showDropdown} center={center} onMouseEnter={() => _handleIn()}>
					{showDropdown && (
						<DropDownItems white={white}>
							{dropdown.items.map((link, index) => {
								return (
									<DropDownItemLvl2 key={index} className='m-b-4' white={white}>
										<Link
											color={extended.purple.dark}
											colorHover={primary.purple}
											fontSize={fontSize}
											fontSizeHover={fontSizeHover}
											link={link}
										/>
									</DropDownItemLvl2>
								)
							})}
						</DropDownItems>
					)}
				</DropDownItemsWrap>
			</DropDownItemLvl1>
		</DropDownWrapper>
	)
}

export default DropDown
